import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { NotificationRecipient } from '../../../../projects/client';

import { AppState } from '../../app.state';

import {
  CentralMailStatus,
  NotificationRecipientsStatus,
  NotificationsState,
} from './notifications.state';

export namespace NotificationsSelectors {
  export const notificationsState = (state: AppState): NotificationsState => state.notifications;

  export function getEmail(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(notificationsState, (state: NotificationsState) => state.email);
  }

  export function notificationsEnabled(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(notificationsState, (state: NotificationsState) => state.notifications);
  }

  export function isLoading(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(notificationsState, (state: NotificationsState) => state.loading);
  }

  export function isLoaded(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(notificationsState, (state: NotificationsState) => state.loaded);
  }

  export const getCentralMailStatus = createSelector(
    notificationsState,
    (state: NotificationsState): CentralMailStatus => {
      const isDataLoading = state.centralMailState.loading;
      const isDataLoaded = state.centralMailState.loaded;
      const isDataPresent = state.centralMailState.dataPresent;
      const isError = state.centralMailState.errorGET;

      const statusDefault = !isDataLoading && !isDataLoaded && !isDataPresent && !isError;
      const statusLoading = isDataLoading && !isDataPresent && !isError;
      const statusLoaded = isDataPresent && isDataLoaded && !isDataLoading && !isError;
      const statusError = isError;

      if (statusDefault) {
        return CentralMailStatus.Default;
      } else if (statusLoading) {
        return CentralMailStatus.Loading;
      } else if (statusLoaded) {
        return CentralMailStatus.Loaded;
      } else if (statusError) {
        return CentralMailStatus.Error;
      }

      return CentralMailStatus.Default;
    }
  );

  export const getNotificationRecipientsStatus = createSelector(
    notificationsState,
    (state: NotificationsState): NotificationRecipientsStatus => {
      const isDataLoading = state.notificationRecipientsState.loading;
      const isDataLoaded = state.notificationRecipientsState.loaded;
      const isDataPresent = state.notificationRecipientsState.dataPresent;
      const isError = state.notificationRecipientsState.errorGET;

      const statusDefault = !isDataLoading && !isDataLoaded && !isDataPresent && !isError;
      const statusLoading = isDataLoading && !isDataPresent;
      const statusLoaded = isDataPresent && isDataLoaded && !isDataLoading && !isError;
      const statusError = isError;
      const centralEmailRequiredError =
        state.notificationRecipientsState.errorGET?.errorType ===
        'CENTRAL_INBOX_SETTINGS_NOT_FOUND';

      if (statusDefault) {
        return NotificationRecipientsStatus.Default;
      } else if (statusLoading) {
        return NotificationRecipientsStatus.Loading;
      } else if (statusLoaded) {
        return NotificationRecipientsStatus.Loaded;
      } else if (statusError && centralEmailRequiredError) {
        return NotificationRecipientsStatus.ErrorCentralEmailRequired;
      } else if (statusError) {
        return NotificationRecipientsStatus.Error;
      }

      return NotificationRecipientsStatus.Default;
    }
  );

  export const getCentralEmail = createSelector(
    notificationsState,
    (state: NotificationsState): string => {
      return state.centralMailState.data?.email;
    }
  );

  export const getNotificationRecipients = createSelector(
    notificationsState,
    (state: NotificationsState): NotificationRecipient[] => {
      return state.notificationRecipientsState.data;
    }
  );
}
